import {UseMutationResult, useMutation, useQuery, useQueryClient} from 'react-query';

import {
  ApiNotificationPreference,
  CreateNotificationPreference,
  createNotificationPref,
  deleteNotificationPref,
  listNotificationPreferences,
  updateNotificationPref,
} from 'src/lib/api/preferences';

export function useNotificationPreferences(type: ApiNotificationPreference['type']) {
  const {data: notificationPreferences, isLoading} = useQuery(
    ['notificationPreferences', type],
    () => listNotificationPreferences(type)
  );
  return {notificationPreferences, isLoaded: !isLoading};
}

export function useCreateNotificationPreferenceMutation(): UseMutationResult<
  ApiNotificationPreference,
  Error,
  CreateNotificationPreference,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(
    (data) => {
      return createNotificationPref(data);
    },
    {
      onSuccess: (pref: ApiNotificationPreference) => {
        queryClient.setQueryData(
          ['notificationPreferences', pref.type],
          (prefs: ApiNotificationPreference[] | undefined) => [...(prefs || []), pref]
        );
        queryClient.invalidateQueries(['notificationPreferences', pref.type]);
      },
    }
  );
}

export function useUpdateNotificationPreferenceMutation(): UseMutationResult<
  ApiNotificationPreference,
  Error,
  ApiNotificationPreference,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(
    (data) => {
      return updateNotificationPref(data);
    },
    {
      onSuccess: (data: ApiNotificationPreference) => {
        queryClient.setQueryData(
          ['notificationPreferences', data.type],
          (prefs: ApiNotificationPreference[] | undefined) =>
            prefs ? prefs.map((p) => (p.id === data.id ? data : p)) : [data]
        );
        queryClient.invalidateQueries(['notificationPreferences', data.type]);
      },
    }
  );
}

type DeleteData = Pick<ApiNotificationPreference, 'id'>;

export function useDeleteNotificationPreferenceMutation(): UseMutationResult<
  ApiNotificationPreference,
  Error,
  DeleteData,
  () => void
> {
  const queryClient = useQueryClient();

  return useMutation(({id}: DeleteData) => deleteNotificationPref(id), {
    onSuccess: (deleted: ApiNotificationPreference) => {
      queryClient.setQueryData(
        ['notificationPreferences', deleted.type],
        (prefs: ApiNotificationPreference[] | undefined) => {
          if (!prefs) return [];
          return prefs.filter((p) => p.id !== deleted.id);
        }
      );
      queryClient.invalidateQueries(['notificationPreferences', deleted.type]);
    },
  });
}
